const RECORD_GROUPS = [
  { key: 'ownership', label: 'Ownership', tab: 'ownership', detail: 'deed and owner-chain records' },
  { key: 'mortgages', label: 'Mortgage & lien', tab: 'mortgages', detail: 'mortgage and lien filings' },
  { key: 'sales', label: 'Sales', tab: 'sales', detail: 'sale records and comparable context' },
  { key: 'permits', label: 'Permits', tab: 'permits', detail: 'DOB permit and job filings' },
  { key: 'violations', label: 'Violations', tab: 'violations', detail: 'ECB and violation signals' },
  { key: 'complaints', label: '311 complaints', tab: 'complaints', detail: 'complaint history' },
  { key: 'health', label: 'Health records', tab: 'health', detail: 'health and inspection context' },
];

function safeCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function getUpgradeTrigger(records, total, top, isPro) {
  const riskTabs = new Set(['ownership', 'mortgages', 'violations', 'permits']);
  const riskRecord = records.find((record) => riskTabs.has(record.tab));

  if (!top) {
    return {
      label: isPro ? 'Packet status' : 'Upgrade trigger',
      status: isPro ? 'Baseline saved' : 'Free screen is enough for now',
      tone: 'neutral',
      reason: 'No follow-up record counts are showing yet, so the free parcel screen can stay as the working baseline.',
      next_step: isPro
        ? 'Keep the property in your notes and rerun it when a new filing, complaint, or permit appears.'
        : 'Use Free until a record count appears or the address becomes serious enough for attorney, lender, or bid review.',
    };
  }

  if (total >= 10 || (riskRecord && riskRecord.count >= 3)) {
    return {
      label: isPro ? 'Packet status' : 'Upgrade trigger',
      status: isPro ? 'Clear before relying on it' : 'Unlock before offer',
      tone: 'high',
      reason: `${total} follow-up records are enough to make this a due-diligence packet, not just a free parcel screen.`,
      next_step: isPro
        ? `Clear ${top.label} first, then save the remaining record notes before using the property in a bid, tour, or client recommendation.`
        : `Open Pro when this address moves into bid, attorney-review, lender, or client-decision territory.`,
    };
  }

  if (total >= 3 || riskRecord) {
    return {
      label: isPro ? 'Packet status' : 'Upgrade trigger',
      status: isPro ? 'Review before next step' : 'Upgrade if the address is serious',
      tone: 'medium',
      reason: 'The record trail is active enough that a serious buyer should know what sits behind the locked tabs before moving forward.',
      next_step: isPro
        ? `Review ${top.label} now and use the rest of the record counts as a checklist.`
        : 'Stay free for casual screening, but unlock before scheduling costly diligence or sending this to a stakeholder.',
    };
  }

  return {
    label: isPro ? 'Packet status' : 'Upgrade trigger',
    status: isPro ? 'Light review' : 'Free-first screen',
    tone: 'good',
    reason: 'Only a small follow-up trail is showing, so the upgrade decision can stay tied to deal seriousness.',
    next_step: isPro
      ? `Open ${top.label} when you want the supporting record detail.`
      : 'Use Free for early comparison, then unlock if this becomes one of the addresses you are actually underwriting.',
  };
}

export function getRecordCountActionPlan(counts = {}, property = {}, userTier = 'free') {
  const records = RECORD_GROUPS
    .map((group) => ({
      ...group,
      count: safeCount(counts[group.key]),
    }))
    .filter((group) => group.count > 0)
    .sort((a, b) => b.count - a.count || RECORD_GROUPS.findIndex((g) => g.key === a.key) - RECORD_GROUPS.findIndex((g) => g.key === b.key));

  const total = records.reduce((sum, group) => sum + group.count, 0);
  const top = records[0] || null;
  const isPro = userTier !== 'free';
  const address = property.address || 'this property';

  if (!top) {
    return {
      title: 'Record-Backed Action Plan',
      status: 'Free screen complete',
      tone: 'neutral',
      summary: `No follow-up record sets are showing yet for ${address}. Keep the parcel identity, zoning, tax, and building basics with your notes before you move on.`,
      total_records: 0,
      top_records: [],
      next_step: {
        label: 'Save the baseline',
        detail: 'Use the free overview as the starting point, then rerun the property if new filings or complaints appear later.',
        tab: 'overview',
      },
      upgrade_trigger: getUpgradeTrigger([], 0, null, isPro),
      cta: isPro ? null : {
        label: 'Compare Pro Coverage',
        href: '/pricing.html?from=record-action-plan',
      },
    };
  }

  return {
    title: 'Record-Backed Action Plan',
    status: isPro ? 'Review highest-volume records first' : 'Records found behind Pro',
    tone: top.count >= 10 ? 'high' : (top.count >= 3 ? 'medium' : 'good'),
    summary: `${total} official follow-up record${total === 1 ? '' : 's'} surfaced for ${address}. Start with the largest record set, then clear the remaining tabs before relying on a bid, tour, or client recommendation.`,
    total_records: total,
    top_records: records.slice(0, 3).map(({ label, tab, detail, count }) => ({ label, tab, detail, count })),
    next_step: {
      label: top.label,
      detail: isPro
        ? `Open ${top.label} first to review ${top.count} ${top.detail}, then work down the remaining record sets.`
        : `Unlock Pro before acting on this property so you can review ${top.count} ${top.detail}.`,
      tab: top.tab,
    },
    upgrade_trigger: getUpgradeTrigger(records, total, top, isPro),
    cta: isPro ? {
      label: `Open ${top.label}`,
      href: `#${top.tab}`,
    } : {
      label: 'Unlock This Property',
      href: `/pricing.html?from=record-action-plan&tab=${encodeURIComponent(top.tab)}${property.bbl ? `&bbl=${encodeURIComponent(String(property.bbl))}` : ''}`,
    },
  };
}
